/**
 * Archive — /archive route. Redesigned Feb 2026 to inherit the homepage
 * design lock. Functionality (search / filter / sort / pagination / URL
 * sync) is preserved 1:1 with the previous implementation.
 *
 * Sections (top → bottom):
 *   1. ArchiveHero          — banner mirroring homepage typography
 *   2. ArchiveToolbar       — sticky search + sort + category chips
 *   3. Discovery rails      — Featured / Trending / Recently Updated /
 *                             Oracle Recommended / Newest / Most Viewed
 *                             (only visible when no filters are active)
 *   4. ArchiveDossierGrid   — filtered main grid using DossierCard
 *   5. SubscriberForm       — legacy CTA at the bottom
 */
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Sparkles, TrendingUp, RefreshCw, Plus, Eye, Star } from "lucide-react";

import { useTopicsList } from "../hooks/use-topics";
import { useCategories } from "../hooks/use-categories";
import ArchiveHero from "../components/archive/ArchiveHero";
import ArchiveToolbar from "../components/archive/ArchiveToolbar";
import ArchiveDiscoveryRail from "../components/archive/ArchiveDiscoveryRail";
import ArchiveDossierGrid from "../components/archive/ArchiveDossierGrid";
import { OracleIntelligencePanel } from "../components/archive/OracleIntelligencePanel";
import { SubscriberForm } from "../components/audience/SubscriberForm";
import { EnvironmentalAtmosphere } from "../components/EnvironmentalAtmosphere";
import { setInvestigationTitle } from "../investigation/investigationMemory";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Archive = () => {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "All";
  const search   = params.get("search")   || "";
  const sort     = params.get("sort")     || "controversy";

  const filtersActive = category !== "All" || Boolean(search);

  useEffect(() => {
    setInvestigationTitle(search ? `Archive — “${search}”` : "The Archive");
  }, [search]);

  // Main filtered grid (existing hook — untouched contract)
  const { topics, loading } = useTopicsList({ category, search, sort });
  const { categories } = useCategories();

  // Discovery payload (featured / trending / newest)
  const [discovery, setDiscovery] = useState({ featured: [], trending: [], newest: [] });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/home/featured`);
        if (!cancelled) {
          setDiscovery({
            featured: data.featured || [],
            trending: data.trending || [],
            newest:   data.newest   || [],
          });
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Full corpus (untinted by user filters) so we can derive the discovery
  // rails: Recently Updated, Oracle Recommended, Most Viewed.
  const [fullCorpus, setFullCorpus] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/topics`, { params: { limit: 300 } });
        const list = Array.isArray(data) ? data : (data?.items || []);
        if (!cancelled) setFullCorpus(list);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const recentlyUpdated = useMemo(() => {
    return [...fullCorpus]
      .filter((t) => t.updated_at)
      .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
      .slice(0, 8);
  }, [fullCorpus]);

  const oracleRecommended = useMemo(() => {
    // Highest evidence + source_quality score = "Oracle Recommended"
    return [...fullCorpus]
      .sort((a, b) =>
        ((b.evidence_score || 0) + (b.source_quality_score || 0)) -
        ((a.evidence_score || 0) + (a.source_quality_score || 0))
      )
      .slice(0, 8);
  }, [fullCorpus]);

  const mostViewed = useMemo(() => {
    return [...fullCorpus].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 8);
  }, [fullCorpus]);

  const totalCount = fullCorpus.length || topics.length;
  const sourcesTotal = useMemo(
    () => fullCorpus.reduce((n, t) => n + (t.source_count || 0), 0),
    [fullCorpus]
  );

  const onParamChange = (k, v) => {
    const np = new URLSearchParams(params);
    if (!v || v === "All" || v === "") np.delete(k);
    else np.set(k, v);
    setParams(np);
  };

  const resetFilters = () => setParams(new URLSearchParams());

  return (
    <div data-testid="archive-page" style={{ background: "#0B0A0D" }}>
      <ArchiveHero totalCount={totalCount} sourcesCount={sourcesTotal} />

      <ArchiveToolbar
        search={search}
        sort={sort}
        category={category}
        categories={categories}
        topicCount={topics.length}
        onParamChange={onParamChange}
      />

      {/* Oracle Intelligence — investigative interpretation banner.
          Only renders when the user has submitted a search query.
          Sits above the discovery rails / grid; never interferes with
          the existing filter flow (grid still shows regex-filtered
          results below). */}
      <OracleIntelligencePanel query={search} />

      {/* Discovery rails — only shown when the user hasn't started filtering.
          Once a category or search query is active, we collapse rails so the
          main grid becomes the sole focus. */}
      {!filtersActive && (
        <div data-testid="archive-discovery" className="border-b" style={{ borderColor: "#2D2A26" }}>
          <ArchiveDiscoveryRail
            testid="rail-featured"
            icon={Star}
            title="Featured Investigations"
            subtitle="Hand-picked centerpieces from editorial — the dossiers most worth your time this week."
            items={discovery.featured}
          />
          <ArchiveDiscoveryRail
            testid="rail-trending"
            icon={TrendingUp}
            title="Trending"
            subtitle="Highest reader traction in the last 7 days."
            items={discovery.trending}
          />
          <ArchiveDiscoveryRail
            testid="rail-updated"
            icon={RefreshCw}
            title="Recently Updated"
            subtitle="Dossiers where the file has moved — new sources, new evidence, new updates."
            items={recentlyUpdated}
          />
          <ArchiveDiscoveryRail
            testid="rail-oracle-rec"
            icon={Sparkles}
            title="Oracle Recommended"
            subtitle="Ranked by evidence density and source quality — investigations with the strongest backing."
            items={oracleRecommended}
          />
          <ArchiveDiscoveryRail
            testid="rail-newest"
            icon={Plus}
            title="Newest Additions"
            subtitle="Freshly opened case files."
            items={discovery.newest}
          />
          <ArchiveDiscoveryRail
            testid="rail-most-viewed"
            icon={Eye}
            title="Most Viewed"
            subtitle="What The Archive keeps returning to."
            items={mostViewed}
          />
        </div>
      )}

      {/* Main filtered grid — search results.
          Locked branding: EnvironmentalAtmosphere "operations" @ 0.4 —
          quieter surveillance-room ambience for the results workspace. */}
      <section className="relative overflow-hidden max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <EnvironmentalAtmosphere variant="operations" intensity={0.4} />
        <div className="mb-6 md:mb-8 flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="h-px w-8" style={{ background: "#D92D20" }} />
              <span className="font-mono text-[9px] uppercase tracking-[0.5em] text-[#D92D20]">
                {filtersActive ? "Filtered Results" : "Full Index"}
              </span>
            </div>
            <h2 className="font-display text-2xl md:text-4xl text-[#E4DFD3] leading-tight tracking-tight">
              {filtersActive ? "Matching Dossiers" : "Every Dossier On File"}
            </h2>
          </div>
          {filtersActive && (
            <button
              onClick={resetFilters}
              data-testid="archive-reset-filters"
              className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#A39E93] hover:text-[#E4DFD3]
                         border-b border-[#454038] hover:border-[#E4DFD3] pb-0.5 transition-colors"
            >
              Clear filters →
            </button>
          )}
        </div>

        <ArchiveDossierGrid topics={topics} loading={loading} onClear={resetFilters} />
      </section>

      <div className="border-t" style={{ borderColor: "#2D2A26" }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <SubscriberForm variant="archive" />
        </div>
      </div>
    </div>
  );
};

export default Archive;
